import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getPortalVoiceOptions } from 'src/mentor/services/portalVoiceCallService'
import {
  endPortalVoiceCall,
  extendPortalVoiceCall,
  getPortalVoiceFriendlyErrorMessage,
  getPortalVoiceCallState,
  startPortalVoiceCall,
  subscribePortalVoiceCallState,
} from './portalVoiceCallManager'

function formatRemainingTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0)
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const secs = safeSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

function formatMinutesLabel(seconds) {
  const minutes = Math.max(1, Math.round((Number(seconds) || 0) / 60))
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

function formatElapsedTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0)
  const minutes = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

function getProviderBadge(providerType) {
  if (providerType === 'plivo') {
    return '(P)'
  }
  if (providerType === 'twilio') {
    return '(T)'
  }
  return ''
}

function getCallStatusPresentation(callState = {}) {
  switch (callState?.status) {
    case 'connecting':
      return { label: 'Preparing call', color: 'info' }
    case 'ringing':
      return { label: 'Ringing', color: 'warning' }
    case 'connected':
      return { label: 'Live', color: 'success' }
    case 'reconnecting':
      return { label: 'Reconnecting', color: 'warning' }
    case 'ending':
      return { label: 'Ending', color: 'default' }
    case 'on-hold':
      return { label: 'On Hold', color: 'secondary' }
    case 'ended':
      return { label: 'Ended', color: 'default' }
    default:
      return { label: 'Idle', color: 'default' }
  }
}

function PortalVoiceCallButton({
  actorRole = 'mentor',
  bookedEventId,
  clinicBrandId,
  clinicId,
  practitionerId,
  patientId,
  targetRole,
  eventType,
  eventTypesCsv,
  uiSurface,
  allowedDestinationTypes,
  autoStartWhenSingleRoute = false,
  renderTrigger,
  buttonLabel = 'Call',
  disabled = false,
  iconOnly = false,
  showTooltip = true,
  disableHoverMotion = false,
  size = 'small',
  sx = {},
  rtcConflict = null,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [loadingRoute, setLoadingRoute] = useState('')
  const [rtcConflictDialogOpen, setRtcConflictDialogOpen] = useState(false)
  const [resolvingRtcConflict, setResolvingRtcConflict] = useState(false)
  const [optionsState, setOptionsState] = useState({
    routes: [],
    error: '',
  })
  const [callState, setCallState] = useState(getPortalVoiceCallState())
  const triggerRef = useRef(null)

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])

  const filterRoutes = (routes = []) => {
    if (!Array.isArray(allowedDestinationTypes) || allowedDestinationTypes.length === 0) {
      return routes
    }
    return routes.filter((route) =>
      allowedDestinationTypes.includes(route?.destinationType || route?.id),
    )
  }

  const isCallActive = [
    'connecting',
    'ringing',
    'connected',
    'reconnecting',
    'on-hold',
    'ending',
  ].includes(callState?.status)
  const isBusy = loadingOptions || !!loadingRoute || callState?.status === 'connecting'
  const isRenewing = !!callState?.renewalInFlight
  const hasRtcConflict = !!rtcConflict?.active && !isCallActive
  const callStatusPresentation = getCallStatusPresentation(callState)

  const rtcConflictTitle = rtcConflict?.title || 'End the video call first'
  const rtcConflictDescription =
    rtcConflict?.description ||
    'This phone call needs exclusive microphone access. End your active video call first, then try again.'
  const rtcConflictActionLabel = rtcConflict?.actionLabel || 'End Video Call'
  const rtcConflictSuccessMessage =
    rtcConflict?.successMessage || 'Video call ended. You can place the phone call now.'
  const rtcConflictErrorMessage =
    rtcConflict?.errorMessage || 'Unable to end the active video call right now.'

  const triggerLabel = useMemo(() => {
    if (!isCallActive) {
      return buttonLabel
    }
    if (callState?.status === 'connecting') {
      return 'Connecting...'
    }
    if (callState?.remainingSeconds > 0) {
      return `Ends in ${formatRemainingTime(callState.remainingSeconds)}`
    }
    return 'Call Active'
  }, [buttonLabel, callState?.remainingSeconds, callState?.status, isCallActive])

  const visibleRoutes = useMemo(() => {
    return filterRoutes(optionsState?.routes || [])
  }, [allowedDestinationTypes, optionsState?.routes])

  async function loadRoutes() {
    const hasClinicContext = !!(clinicBrandId && clinicId)
    const hasSupportTarget = actorRole === 'support-agent' && !!(patientId || practitionerId)

    if (!bookedEventId && !hasClinicContext && !hasSupportTarget) {
      return {
        routes: [],
        error: '',
      }
    }
    setLoadingOptions(true)
    const result = await getPortalVoiceOptions({
      actorRole,
      bookedEventId,
      clinicBrandId,
      clinicId,
      practitionerId,
      patientId,
      targetRole,
      eventType,
      eventTypesCsv,
    })
    setOptionsState({
      routes: result?.routes || [],
      error: result?.ok ? '' : result?.message || 'Unable to load portal call routes.',
    })
    setLoadingOptions(false)
    return {
      routes: result?.routes || [],
      error: result?.ok ? '' : result?.message || 'Unable to load portal call routes.',
    }
  }

  async function handleTriggerClick(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    if (disabled) {
      return
    }

    if (hasRtcConflict) {
      setRtcConflictDialogOpen(true)
      return
    }

    if (isCallActive) {
      setAnchorEl(event?.currentTarget || null)
      return
    }

    const triggerElement = triggerRef.current || event?.currentTarget || null
    setAnchorEl(triggerElement)
    const loadedState = await loadRoutes()
    const filteredRoutes = filterRoutes(loadedState?.routes || [])

    if (
      autoStartWhenSingleRoute &&
      filteredRoutes.length === 1 &&
      filteredRoutes[0]?.enabled
    ) {
      await handleRouteSelect(filteredRoutes[0])
      return
    }
  }

  async function handleRouteSelect(route) {
    setLoadingRoute(route?.destinationType || route?.id || '')
    setAnchorEl(null)
    try {
      await startPortalVoiceCall({
        actorRole,
        bookedEventId,
        clinicBrandId,
        clinicId,
        practitionerId,
        patientId,
        targetRole,
        eventType,
        eventTypesCsv,
        destinationType: route?.destinationType || route?.id,
        uiSurface,
        providerTypeHint: route?.providerType || null,
      })
    } catch (error) {
      toast.error(
        getPortalVoiceFriendlyErrorMessage(error, 'Unable to place the portal voice call.'),
      )
    } finally {
      setLoadingRoute('')
    }
  }

  async function handleExtendCall() {
    try {
      await extendPortalVoiceCall()
      setAnchorEl(null)
    } catch (error) {
      toast.error(error?.message || 'Unable to extend the portal voice call.')
    }
  }

  function handleEndCall() {
    setAnchorEl(null)
    endPortalVoiceCall()
  }

  async function handleResolveRtcConflict() {
    if (typeof rtcConflict?.onResolve !== 'function') {
      setRtcConflictDialogOpen(false)
      return
    }

    setResolvingRtcConflict(true)
    try {
      await rtcConflict.onResolve()
      setRtcConflictDialogOpen(false)
      toast.info(rtcConflictSuccessMessage)
    } catch (error) {
      toast.error(error?.message || rtcConflictErrorMessage)
    } finally {
      setResolvingRtcConflict(false)
    }
  }

  const triggerProps = {
    onClick: handleTriggerClick,
    label: triggerLabel,
    busy: isBusy,
    active: isCallActive,
    disabled,
  }

  const hoverMotionStyles = disableHoverMotion
    ? {}
    : {
        transform: 'translateY(-1px)',
      }

  const iconButtonNode = (
    <IconButton
      ref={triggerRef}
      size={size}
      aria-label={triggerLabel}
      title={showTooltip ? undefined : triggerLabel}
      onClick={handleTriggerClick}
      disabled={disabled}
      sx={{
        width: size === 'large' ? 46 : 40,
        height: size === 'large' ? 46 : 40,
        color: '#ffffff',
        background: isCallActive
          ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
          : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        boxShadow: isCallActive
          ? '0 12px 24px rgba(185, 28, 28, 0.28)'
          : '0 12px 24px rgba(37, 99, 235, 0.24)',
        transition: 'box-shadow 140ms ease, filter 140ms ease, background 140ms ease',
        '&:hover': {
          background: isCallActive
            ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
            : 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
          boxShadow: isCallActive
            ? '0 16px 28px rgba(185, 28, 28, 0.34)'
            : '0 16px 28px rgba(37, 99, 235, 0.3)',
          ...hoverMotionStyles,
        },
        ...sx,
      }}
    >
      {isBusy || isRenewing ? (
        <CircularProgress size={16} sx={{ color: 'inherit' }} />
      ) : isCallActive ? (
        <CallEndRoundedIcon fontSize="small" />
      ) : (
        <PhoneInTalkRoundedIcon fontSize="small" />
      )}
    </IconButton>
  )

  const triggerNode = renderTrigger ? (
    renderTrigger(triggerProps)
  ) : iconOnly ? (
    showTooltip ? (
      <Tooltip
        title={triggerLabel}
        arrow
        enterDelay={650}
        enterNextDelay={650}
        disableInteractive
        disableHoverListener={Boolean(anchorEl)}
      >
        {disabled ? <span>{iconButtonNode}</span> : iconButtonNode}
      </Tooltip>
    ) : disabled ? (
      <span>{iconButtonNode}</span>
    ) : (
      iconButtonNode
    )
  ) : (
    <Button
      ref={triggerRef}
      size={size}
      variant="contained"
      color={isCallActive ? 'error' : 'primary'}
      onClick={handleTriggerClick}
      disabled={disabled}
      startIcon={
        isBusy || isRenewing ? (
          <CircularProgress size={14} color="inherit" />
        ) : isCallActive ? (
          <CallEndRoundedIcon />
        ) : (
          <PhoneInTalkRoundedIcon />
        )
      }
      sx={{
        minHeight: 38,
        px: 1.75,
        borderRadius: '999px',
        textTransform: 'none',
        fontWeight: 700,
        letterSpacing: '0.01em',
        color: '#ffffff',
        background: isCallActive
          ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
          : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        boxShadow: isCallActive
          ? '0 14px 28px rgba(185, 28, 28, 0.22)'
          : '0 14px 28px rgba(37, 99, 235, 0.2)',
        '&:hover': {
          background: isCallActive
            ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
            : 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
          boxShadow: isCallActive
            ? '0 16px 30px rgba(185, 28, 28, 0.28)'
            : '0 16px 30px rgba(37, 99, 235, 0.26)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.72)',
          background: 'linear-gradient(135deg, rgba(148, 163, 184, 0.9) 0%, rgba(100, 116, 139, 0.9) 100%)',
        },
        ...sx,
      }}
    >
      {triggerLabel}
    </Button>
  )

  return (
    <>
      {triggerNode}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            minWidth: 300,
            borderRadius: '14px',
            boxShadow: '0 14px 34px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        {isCallActive ? (
          <Box sx={{ px: 2, py: 1.75, maxWidth: 360 }}>
            <Stack spacing={1.5}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Portal Call In Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[callState?.destinationLabel, callState?.maskedDestination]
                    .filter(Boolean)
                    .join(' • ') || 'Connected participant'}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={callStatusPresentation.color}
                  label={callStatusPresentation.label}
                />
                <Chip
                  size="small"
                  color={callState?.remainingSeconds > 0 ? 'warning' : 'default'}
                  label={
                    callState?.remainingSeconds > 0
                      ? `Ends in ${formatRemainingTime(callState.remainingSeconds)}`
                      : 'Ending soon'
                  }
                />
                {callState?.recordingEnabled ? (
                  <Chip size="small" color="info" variant="outlined" label="Recording enabled" />
                ) : null}
                {callState?.providerType ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getProviderBadge(callState.providerType)}
                  />
                ) : null}
                {callState?.status === 'connected' && callState?.elapsedSeconds >= 0 ? (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`Live ${formatElapsedTime(callState.elapsedSeconds)}`}
                  />
                ) : null}
                {callState?.renewalEnabled ? (
                  <Chip
                    size="small"
                    color={callState?.renewalAllowed ? 'success' : 'default'}
                    variant="outlined"
                    label={
                      callState?.renewalAllowed
                        ? `Can extend by ${formatMinutesLabel(callState?.renewalIncrementSeconds)}`
                        : 'Max call duration reached'
                    }
                  />
                ) : null}
              </Stack>

              {callState?.error ? (
                <Typography variant="body2" color="error.main">
                  {callState.error}
                </Typography>
              ) : null}

              <Divider />

              <Stack direction="row" spacing={1.25} justifyContent="flex-end">
                {callState?.renewalEnabled ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExtendCall}
                    disabled={!callState?.renewalAllowed || isRenewing}
                  >
                    {isRenewing
                      ? 'Extending...'
                      : `Extend by ${formatMinutesLabel(callState?.renewalIncrementSeconds)}`}
                  </Button>
                ) : null}
                <Button size="small" color="error" variant="contained" onClick={handleEndCall}>
                  End Call
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : loadingOptions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading call routes...</Typography>
          </Box>
        ) : optionsState?.error ? (
          <Box sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
            <Typography variant="body2" color="error.main">
              {optionsState.error}
            </Typography>
          </Box>
        ) : visibleRoutes.length > 0 ? (
          visibleRoutes.map((route) => {
            const destinationType = route?.destinationType || route?.id
            const secondaryText = route?.enabled
              ? [
                  getProviderBadge(route?.providerType),
                  route?.destinationLabel,
                  route?.maskedDestination,
                ]
                  .filter(Boolean)
                  .join(' • ')
              : route?.disabledReason || 'This route is currently unavailable.'

            return (
              <MenuItem
                key={destinationType}
                onClick={() => route?.enabled && handleRouteSelect(route)}
                disabled={!route?.enabled || !!loadingRoute}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.5,
                  whiteSpace: 'normal',
                }}
              >
                <ListItemText
                  primary={route?.title || route?.destinationLabel || destinationType}
                  secondary={secondaryText}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{
                    sx: {
                      color: route?.enabled ? 'text.secondary' : 'warning.dark',
                      mt: 0.25,
                    },
                  }}
                />
              </MenuItem>
            )
          })
        ) : (
          <Box sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
            <Typography variant="body2" color="text.secondary">
              No portal call routes are available here right now.
            </Typography>
          </Box>
        )}
      </Menu>
      <Dialog
        open={rtcConflictDialogOpen}
        onClose={() => !resolvingRtcConflict && setRtcConflictDialogOpen(false)}
        PaperProps={{
          sx: {
            maxWidth: 420,
            width: 'calc(100% - 32px)',
            borderRadius: '18px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={1}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={rtcConflict?.badgeLabel || 'Video Call Active'}
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {rtcConflictTitle}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '4px !important' }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {rtcConflictDescription}
            </Typography>
            {rtcConflict?.helperText ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '12px',
                  background: 'rgba(37, 99, 235, 0.06)',
                  border: '1px solid rgba(37, 99, 235, 0.12)',
                }}
              >
                <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                  {rtcConflict.helperText}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            variant="text"
            onClick={() => setRtcConflictDialogOpen(false)}
            disabled={resolvingRtcConflict}
          >
            Not now
          </Button>
          {typeof rtcConflict?.onResolve === 'function' ? (
            <Button
              variant="contained"
              color="error"
              onClick={handleResolveRtcConflict}
              disabled={resolvingRtcConflict}
            >
              {resolvingRtcConflict ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                rtcConflictActionLabel
              )}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default memo(PortalVoiceCallButton)
